import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ShieldCheck, ShieldAlert, Clock, FileText, BookOpen, 
  User, Building, FileCheck, Check, X, ExternalLink, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const OWNERSHIP_LABELS = {
  'original_author': 'Autor Original',
  'publisher': 'Editorial',
  'rights_holder': 'Titular de Derechos'
};

const AuthorVerificationAdmin = ({ token }) => {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchVerifications();
  }, [filter]);

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/api/admin/author-verifications?status=${filter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVerifications(response.data);
    } catch (error) {
      console.error('Error fetching verifications:', error);
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (action) => {
    if (!selectedVerification) return;
    
    if (action === 'reject' && !rejectionReason.trim()) {
      toast.error('Debes proporcionar un motivo de rechazo');
      return;
    }

    setProcessing(true);
    try {
      await axios.post(
        `${API}/api/admin/review-author-verification`,
        {
          request_id: selectedVerification.id,
          action,
          rejection_reason: action === 'reject' ? rejectionReason : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(action === 'approve' ? 'Verificación aprobada' : 'Verificación rechazada');
      setSelectedVerification(null);
      setRejectionReason('');
      fetchVerifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-400">Pendiente</span>;
      case 'approved':
        return <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-400">Aprobada</span>;
      case 'rejected':
        return <span className="px-2 py-1 rounded text-xs bg-red-500/20 text-red-400">Rechazada</span>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="gotham-bold text-2xl text-white flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-purple-400" />
          Verificación de Autores
        </h2>
        
        {/* Filter */}
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected', 'all'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? 'btn-viubux' : 'border-white/20 text-white/60'}
            >
              {f === 'pending' && 'Pendientes'}
              {f === 'approved' && 'Aprobadas'}
              {f === 'rejected' && 'Rechazadas'}
              {f === 'all' && 'Todas'}
            </Button>
          ))}
        </div>
      </div>

      {/* Verifications List */}
      <Card className="card-viubux-dark border-purple-500/30">
        <CardContent className="pt-6">
          {loading ? (
            <p className="text-white/60 text-center py-8">Cargando...</p>
          ) : verifications.length === 0 ? (
            <div className="text-center py-12">
              <ShieldCheck className="w-16 h-16 mx-auto text-white/20 mb-4" />
              <p className="text-white/60">No hay solicitudes {filter !== 'all' ? filter === 'pending' ? 'pendientes' : filter === 'approved' ? 'aprobadas' : 'rechazadas' : ''}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {verifications.map((v) => (
                <div
                  key={v.id}
                  className="bg-black/20 rounded-xl p-4 border border-white/5 hover:border-purple-500/30 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="gotham-bold text-lg text-white">{v.user_name}</h3>
                        {getStatusBadge(v.status)}
                      </div>
                      
                      <p className="text-white/60 text-sm mb-1">{v.user_email}</p>
                      
                      <div className="flex items-center gap-4 text-white/40 text-xs mt-2">
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-3 h-3" />
                          {v.work_title}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {OWNERSHIP_LABELS[v.ownership_type] || v.ownership_type}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(v.created_at)}
                        </span>
                      </div>

                      {v.status === 'rejected' && v.rejection_reason && (
                        <div className="mt-2 p-2 bg-red-500/10 rounded border border-red-500/20">
                          <p className="text-red-300 text-xs">
                            <strong>Motivo:</strong> {v.rejection_reason}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedVerification(v)}
                        className="border-white/20 text-white/60 hover:text-white"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      
                      {v.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedVerification(v);
                              handleReview('approve');
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setSelectedVerification(v)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail/Rejection Modal */}
      {selectedVerification && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <h3 className="gotham-bold text-xl text-white">Detalle de Solicitud</h3>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/40 text-xs">Nombre</label>
                  <p className="text-white">{selectedVerification.user_name}</p>
                </div>
                <div>
                  <label className="text-white/40 text-xs">Email</label>
                  <p className="text-white">{selectedVerification.user_email}</p>
                </div>
              </div>

              <div>
                <label className="text-white/40 text-xs">Título de la Obra</label>
                <p className="text-white gotham-bold">{selectedVerification.work_title}</p>
              </div>

              {selectedVerification.work_identifier && (
                <div>
                  <label className="text-white/40 text-xs">ISBN/Identificador</label>
                  <p className="text-white">{selectedVerification.work_identifier}</p>
                </div>
              )}

              {selectedVerification.work_url && (
                <div>
                  <label className="text-white/40 text-xs">Link de la Obra</label>
                  <a 
                    href={selectedVerification.work_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 flex items-center gap-1"
                  >
                    {selectedVerification.work_url}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}

              <div>
                <label className="text-white/40 text-xs">Tipo de Titularidad</label>
                <p className="text-white">{OWNERSHIP_LABELS[selectedVerification.ownership_type]}</p>
              </div>

              {selectedVerification.additional_notes && (
                <div>
                  <label className="text-white/40 text-xs">Notas Adicionales</label>
                  <p className="text-white/70 text-sm">{selectedVerification.additional_notes}</p>
                </div>
              )}

              <div>
                <label className="text-white/40 text-xs">Fecha de Solicitud</label>
                <p className="text-white">{formatDate(selectedVerification.created_at)}</p>
              </div>

              {/* Actions for pending */}
              {selectedVerification.status === 'pending' && (
                <div className="pt-4 border-t border-white/10 space-y-4">
                  <div>
                    <label className="text-white/70 text-sm mb-2 block">
                      Motivo de rechazo (solo si rechazas)
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-purple-500 min-h-[80px]"
                      placeholder="Ej: No se encontró la obra en app.viubux.com..."
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleReview('reject')}
                      disabled={processing}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Rechazar
                    </Button>
                    <Button
                      onClick={() => handleReview('approve')}
                      disabled={processing}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Aprobar
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-white/10">
              <Button
                onClick={() => {
                  setSelectedVerification(null);
                  setRejectionReason('');
                }}
                variant="outline"
                className="w-full border-purple-500/50 text-white"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthorVerificationAdmin;
